import { MathUtils, Square, Triangle } from './math-utils.js';
import { MATH_CONSTANTS, FRACTAL_CONSTANTS } from './constants.js';
import { domCache, DOMUtils } from './dom-cache.js';

/**
 * Fractal engine for generating Pythagoras Trees
 */
export class FractalEngine {
    /**
     * Create a fractal engine
     * @param {Object} config - Configuration object
     */
    constructor(config = {}) {
        this.config = config;
    }

    /**
     * Generate complete Pythagoras Tree
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle in degrees
     * @param {number} depth - Maximum recursion depth
     * @returns {Array} Array of all squares in the tree
     */
    generateTree(baseSquare, triangleAngle, depth) {
        // For very deep trees, use the iterative approach to avoid stack overflow
        if (depth >= 12) {
            return this.generateTreeIterative(baseSquare, triangleAngle, depth);
        }

        // For shallower trees, use the original recursive approach
        const allSquares = [];
        this._generateRecursive(baseSquare, triangleAngle, depth, allSquares);
        return allSquares;
    }

    /**
     * Generate tree using an iterative approach
     * (prevents stack overflow for deep trees)
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle in degrees
     * @param {number} maxDepth - Maximum recursion depth
     * @returns {Array} Array of all squares in the tree
     */
    generateTreeIterative(baseSquare, triangleAngle, maxDepth) {
        const allSquares = [baseSquare];

        // Use a queue to simulate recursion without deep call stacks
        const queue = [{ square: baseSquare, depth: maxDepth }];

        while (queue.length > 0) {
            const { square, depth } = queue.shift();

            // Stop if we've reached the maximum depth
            if (depth <= 0) {
                continue;
            }

            // Create triangle on top of this square
            const triangle = this.createTriangleOnSquare(square, triangleAngle);

            // Create two new squares on the triangle's sides
            const newSquares = this.createSquaresFromTriangle(triangle, square.level + 1);

            // Add the new squares to the results and queue
            for (const newSquare of newSquares) {
                allSquares.push(newSquare);
                queue.push({ square: newSquare, depth: depth - 1 });
            }
        }

        return allSquares;
    }

    /**
     * Generate complete Pythagoras Tree with both squares and triangles
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle in degrees
     * @param {number} depth - Maximum recursion depth
     * @param {boolean} endWithTriangles - Whether to end with triangles instead of squares
     * @returns {Object} Object with squares and triangles arrays
     */
    generateTreeWithTriangles(baseSquare, triangleAngle, depth, endWithTriangles = false) {
        // For very deep trees, use the iterative approach to avoid stack overflow
        if (depth >= 12) {
            // Avoid console.log for performance with large trees
            return this.generateTreeWithTrianglesIterative(baseSquare, triangleAngle, depth, endWithTriangles);
        }

        // For shallower trees, use the original recursive approach
        const allSquares = [];
        const allTriangles = [];
        this._generateRecursiveWithTriangles(
            baseSquare,
            triangleAngle,
            depth,
            allSquares,
            allTriangles,
            endWithTriangles
        );
        return { squares: allSquares, triangles: allTriangles };
    }

    /**
     * Generate tree with squares and triangles using an iterative approach
     * (prevents stack overflow for deep trees)
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle in degrees
     * @param {number} maxDepth - Maximum recursion depth
     * @param {boolean} endWithTriangles - Whether to end with triangles instead of squares
     * @returns {Object} Object with squares and triangles arrays
     */
    generateTreeWithTrianglesIterative(baseSquare, triangleAngle, maxDepth, endWithTriangles = false) {
        const allSquares = [baseSquare];
        const allTriangles = [];

        // Use a queue to simulate recursion without deep call stacks
        const queue = [{ square: baseSquare, depth: maxDepth }];

        while (queue.length > 0) {
            const { square, depth } = queue.shift();

            // Stop if we've reached the maximum depth
            if (depth <= 0) {
                continue;
            }

            // Create triangle on top of this square
            const triangle = this.createTriangleOnSquare(square, triangleAngle);
            allTriangles.push(triangle);

            // If ending with triangles and this is the final level, don't create more squares
            if (endWithTriangles && depth === 1) {
                continue;
            }

            // Create two new squares on the triangle's sides
            const newSquares = this.createSquaresFromTriangle(triangle, square.level + 1);

            // Add the new squares to the results and queue
            for (const newSquare of newSquares) {
                allSquares.push(newSquare);
                queue.push({ square: newSquare, depth: depth - 1 });
            }
        }

        return { squares: allSquares, triangles: allTriangles };
    }

    /**
     * Generate squares only at a specific level (for animation)
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle in degrees
     * @param {number} targetLevel - Target level to generate
     * @returns {Array} Array of squares at the target level
     */
    generateLevel(baseSquare, triangleAngle, targetLevel) {
        if (targetLevel === 0) {
            return [baseSquare];
        }

        // Use breadth-first approach to generate specific level
        let currentLevelSquares = [baseSquare];

        for (let level = 0; level < targetLevel; level++) {
            const nextLevelSquares = [];

            currentLevelSquares.forEach(square => {
                if (square.level === level) {
                    const triangle = this.createTriangleOnSquare(square, triangleAngle);
                    const newSquares = this.createSquaresFromTriangle(triangle, level + 1);
                    nextLevelSquares.push(...newSquares);
                }
            });

            currentLevelSquares = nextLevelSquares;
        }

        return currentLevelSquares;
    }

    /**
     * Generate all squares up to a specified level
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle in degrees
     * @param {number} maxLevel - Maximum level to generate
     * @returns {Array} Array of all squares up to maxLevel
     */
    generateUpToLevel(baseSquare, triangleAngle, maxLevel) {
        const allSquares = [];

        for (let level = 0; level <= maxLevel; level++) {
            const levelSquares = this.generateLevel(baseSquare, triangleAngle, level);
            allSquares.push(...levelSquares);
        }

        return allSquares;
    }

    /**
     * Generate both squares and triangles up to a specified level
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle in degrees
     * @param {number} maxLevel - Maximum level to generate
     * @returns {Object} Object with squares and triangles arrays
     */
    generateUpToLevelWithTriangles(baseSquare, triangleAngle, maxLevel) {
        const allSquares = [];
        const allTriangles = [];

        for (let level = 0; level <= maxLevel; level++) {
            const levelSquares = this.generateLevel(baseSquare, triangleAngle, level);
            allSquares.push(...levelSquares);

            // Generate triangles for this level (except the last level)
            if (level < maxLevel) {
                levelSquares.forEach(square => {
                    if (square.level === level) {
                        const triangle = this.createTriangleOnSquare(square, triangleAngle);
                        allTriangles.push(triangle);
                    }
                });
            }
        }

        return { squares: allSquares, triangles: allTriangles };
    }

    /**
     * Create RIGHT triangle on top of a square (FIXED FOR CANVAS COORDINATES)
     * @param {Square} square - Base square
     * @param {number} triangleAngleDegrees - Angle at left base vertex in degrees
     * @returns {Triangle} Triangle object
     */
    createTriangleOnSquare(square, triangleAngleDegrees) {
        const angleRad = MathUtils.degreesToRadians(triangleAngleDegrees);

        // Get top edge of square (shared with triangle base)
        const topLeft = square.corners[0]; // Left vertex (shared)
        const topRight = square.corners[1]; // Right vertex (shared)

        const baseLength = MathUtils.distance(topLeft, topRight);
        const baseDirection = Math.atan2(topRight.y - topLeft.y, topRight.x - topLeft.x);

        // CORRECTED: Account for canvas coordinate system (Y increases downward)
        // For a right triangle positioned ABOVE the square:
        // - Base: topLeft to topRight (shared with square's top edge)
        // - Angle at topLeft = triangleAngleDegrees
        // - Angle at topRight = 90° - triangleAngleDegrees
        // - Angle at apex = 90° (right angle, positioned above square)

        // Calculate the height and position of the apex
        const height = baseLength * Math.sin(angleRad) * Math.cos(angleRad);
        const distanceFromLeft = baseLength * Math.cos(angleRad) * Math.cos(angleRad);

        // Calculate foot of perpendicular from apex to base
        const footPoint = {
            x: topLeft.x + distanceFromLeft * Math.cos(baseDirection),
            y: topLeft.y + distanceFromLeft * Math.sin(baseDirection),
        };

        // FIXED: Apex direction should be UPWARD (negative Y direction in canvas)
        // Since canvas Y increases downward, "above" means subtract from Y
        const perpDirection = baseDirection - MATH_CONSTANTS.ANGLE_90_DEGREES; // 90° clockwise (upward in canvas)

        const apex = {
            x: footPoint.x + height * Math.cos(perpDirection),
            y: footPoint.y + height * Math.sin(perpDirection),
        };

        return new Triangle(topLeft, topRight, apex, square.level);
    }

    /**
     * Create new squares on triangle sides
     * @param {Triangle} triangle - Triangle to build squares on
     * @param {number} level - Recursion level for new squares
     * @returns {Array} Array of two new squares
     */
    createSquaresFromTriangle(triangle, level) {
        const [base1, base2, apex] = triangle.vertices;

        // Left side: from base1 to apex
        const leftSide = {
            start: base1,
            end: apex,
            length: MathUtils.distance(base1, apex),
        };

        // Right side: from apex to base2
        const rightSide = {
            start: apex,
            end: base2,
            length: MathUtils.distance(apex, base2),
        };

        const leftSquare = this._createSquareOnSide(leftSide, level);
        const rightSquare = this._createSquareOnSide(rightSide, level);

        return [leftSquare, rightSquare];
    }

    /**
     * Create a square on a triangle side
     * @private
     * @param {Object} side - Line segment with start, end, length
     * @param {number} level - Recursion level
     * @returns {Square} New square object
     */
    _createSquareOnSide(side, level) {
        const sideLength = side.length;

        // Calculate the angle of the side
        const sideAngle = Math.atan2(side.end.y - side.start.y, side.end.x - side.start.x);

        // Find the midpoint of the side
        const midpoint = {
            x: (side.start.x + side.end.x) / 2,
            y: (side.start.y + side.end.y) / 2,
        };

        // Square center is perpendicular to the side, offset by half the side length
        // Direction perpendicular to the side (outward from triangle)
        const perpAngle = sideAngle - MATH_CONSTANTS.ANGLE_90_DEGREES; // 90 degrees counterclockwise
        const centerOffset = sideLength * FRACTAL_CONSTANTS.SQUARE_CENTER_OFFSET_RATIO;

        const squareCenter = {
            x: midpoint.x + centerOffset * Math.cos(perpAngle),
            y: midpoint.y + centerOffset * Math.sin(perpAngle),
        };

        return new Square(squareCenter.x, squareCenter.y, sideLength, sideAngle, level);
    }

    /**
     * Recursive helper for complete tree generation
     * @private
     */
    _generateRecursive(square, triangleAngle, remainingDepth, allSquares) {
        // Always add the current square first
        allSquares.push(square);

        // Stop if we've reached the maximum depth
        if (remainingDepth <= 0) {
            return;
        }

        // Create triangle on top of this square
        const triangle = this.createTriangleOnSquare(square, triangleAngle);

        // Create two new squares on the triangle's sides
        const newSquares = this.createSquaresFromTriangle(triangle, square.level + 1);

        // Recursively generate subtrees for each new square
        newSquares.forEach(newSquare => {
            this._generateRecursive(newSquare, triangleAngle, remainingDepth - 1, allSquares);
        });
    }

    /**
     * Recursive helper for generating tree with both squares and triangles
     * @private
     */
    _generateRecursiveWithTriangles(
        square,
        triangleAngle,
        remainingDepth,
        allSquares,
        allTriangles,
        endWithTriangles = false
    ) {
        // Always add the current square first
        allSquares.push(square);

        // Stop if we've reached the maximum depth
        if (remainingDepth <= 0) {
            return;
        }

        // Create triangle on top of this square
        const triangle = this.createTriangleOnSquare(square, triangleAngle);
        allTriangles.push(triangle);

        // If ending with triangles and this is the final level, don't create more squares
        if (endWithTriangles && remainingDepth === 1) {
            return;
        }

        // Create two new squares on the triangle's sides
        const newSquares = this.createSquaresFromTriangle(triangle, square.level + 1);

        // Recursively generate subtrees for each new square
        newSquares.forEach(newSquare => {
            this._generateRecursiveWithTriangles(
                newSquare,
                triangleAngle,
                remainingDepth - 1,
                allSquares,
                allTriangles,
                endWithTriangles
            );
        });
    }
}

/**
 * Static fractal controller for non-animated trees
 * Handles preservation and redrawing of trees generated without animation
 */
export class StaticFractal {
    /**
     * Create static fractal controller
     * @param {FractalEngine} engine - Fractal engine instance
     * @param {Object} renderer - Canvas renderer instance
     * @param {Object} config - Configuration object
     */ constructor(engine, renderer, config) {
        this.engine = engine;
        this.renderer = renderer;
        this.config = config;
        this.currentSquares = [];
        this.currentTriangles = [];
        this.isGenerated = false;
    } /**
     * Generate and store static tree with both squares and triangles
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle
     * @param {number} depth - Maximum depth
     */
    generateAndStore(baseSquare, triangleAngle, depth) {
        const endWithTriangles = this.config.endWithTriangles || false;
        const treeData = this.engine.generateTreeWithTriangles(baseSquare, triangleAngle, depth, endWithTriangles);
        this.currentSquares = treeData.squares;
        this.currentTriangles = treeData.triangles;
        this.isGenerated = true;
        this.renderTree();
    }

    /**
     * Render the current static tree
     */
    renderTree() {
        if (this.isGenerated && this.currentSquares.length > 0 && this.renderer) {
            this.renderer.clear();
            this.renderer.renderTree(this.currentSquares, this.currentTriangles, this.config);
        }
    }

    /**
     * Force redraw of current tree (for UI events like panel minimize)
     */
    redraw() {
        this.renderTree();
    } /**
     * Check if tree is generated and ready
     */
    isTreeGenerated() {
        return this.isGenerated && this.currentSquares.length > 0;
    }

    /**
     * Get current tree data
     */
    getCurrentTreeData() {
        return {
            squares: [...this.currentSquares],
            triangles: [...this.currentTriangles],
        };
    }

    /**
     * Clear current tree
     */
    clear() {
        this.currentSquares = [];
        this.currentTriangles = [];
        this.isGenerated = false;
        if (this.renderer) {
            this.renderer.clear();
        }
    }

    /**
     * Update config and re-render if needed
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
        if (this.isGenerated) {
            this.renderTree();
        }
    }
}

/**
 * Animated fractal controller for level-by-level generation
 */
export class AnimatedFractal {
    /**
     * Create animated fractal controller
     * @param {FractalEngine} engine - Fractal engine instance
     * @param {Object} renderer - Canvas renderer instance
     * @param {Object} config - Configuration object
     */
    constructor(engine, renderer, config) {
        this.engine = engine;
        this.renderer = renderer;
        this.config = config;
        this.currentLevel = 0;
        this.allSquares = [];
        this.allTriangles = [];
        this.isAnimating = false;
        this.animationCallbacks = {};
    }

    /**
     * Start level-by-level animation
     * @param {Square} baseSquare - Starting square
     * @param {number} triangleAngle - Triangle angle
     * @param {number} maxDepth - Maximum depth
     * @returns {Promise} Animation completion promise
     */ async startLevelByLevelAnimation(baseSquare, triangleAngle, maxDepth) {
        this.isAnimating = true;
        this.currentLevel = 0;
        this.allSquares = [];
        this.allTriangles = [];

        try {
            const endWithTriangles = this.config.endWithTriangles || false;
            
            while (this.currentLevel <= maxDepth && this.isAnimating) {
                // Generate squares for current level
                const levelSquares = this.engine.generateLevel(
                    baseSquare,
                    triangleAngle,
                    this.currentLevel
                );

                // Show level indicator
                this._showLevelIndicator(this.currentLevel);

                // Animate each polygon in current level
                await this._animateCurrentLevel(levelSquares);

                // Generate triangles for current level
                // If endWithTriangles is false: generate triangles except for the last level (current behavior)
                // If endWithTriangles is true: generate triangles for all levels including the last
                const shouldGenerateTriangles = endWithTriangles ? 
                    this.currentLevel <= maxDepth : 
                    this.currentLevel < maxDepth;
                
                if (shouldGenerateTriangles) {
                    levelSquares.forEach(square => {
                        if (square.level === this.currentLevel) {
                            const triangle = this.engine.createTriangleOnSquare(
                                square,
                                triangleAngle
                            );
                            this.allTriangles.push(triangle);
                        }
                    });

                    // If we just generated triangles and are ending with triangles,
                    // animate the triangles as well
                    if (endWithTriangles && this.currentLevel === maxDepth) {
                        // Render the final frame with triangles
                        this.renderer.clear();
                        this.renderer.renderTree(this.allSquares, this.allTriangles, this.config);
                    }
                }

                // Wait before proceeding to next level
                await this._delay(this.config.levelDelay * (1 / this.config.animationSpeed));

                this.currentLevel++;
            }
        } catch (error) {
            console.error('Animation error:', error);
        } finally {
            this.isAnimating = false;
            this._onAnimationComplete();
        }
    } /**
     * Animate current level squares
     * @private
     */
    async _animateCurrentLevel(levelSquares) {
        for (let i = 0; i < levelSquares.length && this.isAnimating; i++) {
            // Add current square to total collection
            this.allSquares.push(levelSquares[i]);

            // Render current state with both squares and triangles
            this.renderer.clear();
            this.renderer.renderTree(this.allSquares, this.allTriangles, this.config);

            // Wait before showing next polygon
            await this._delay(this.config.polygonDelay * (1 / this.config.animationSpeed));
        }
    }

    /**
     * Show level indicator
     * @private
     */
    _showLevelIndicator(level) {
        if (this.config.showLevelIndicator) {
            const indicator = domCache.get('level-indicator');
            const levelSpan = domCache.get('current-level');
            if (indicator && levelSpan) {
                DOMUtils.setText('current-level', level.toString());
                DOMUtils.removeClass('level-indicator', 'hidden');
            }
        }
    } /**
     * Get current tree state for external rendering
     * @returns {Object} Current tree data with squares and triangles
     */
    getCurrentTreeData() {
        return {
            squares: [...this.allSquares],
            triangles: [...this.allTriangles],
        };
    }

    /**
     * Check if animation has completed and tree data is available
     * @returns {boolean} True if tree is complete and ready for static rendering
     */
    isTreeComplete() {
        return !this.isAnimating && this.allSquares.length > 0;
    } /**
     * Render the current tree state (for external use after UI events)
     * This method should be called by the main application when UI events
     * (like panel minimize/resize) clear the canvas and need to redraw the static tree
     */
    renderCurrentTree() {
        if (this.allSquares.length > 0 && this.renderer) {
            this.renderer.clear();
            this.renderer.renderTree(this.allSquares, this.allTriangles, this.config);
        }
    }

    /**
     * Force redraw of completed tree
     * Useful when canvas has been cleared by external events
     */
    redrawCompletedTree() {
        if (this.isTreeComplete()) {
            this.renderCurrentTree();
        }
    } /**
     * Ensure tree is rendered after any interaction
     * This should be called by interaction handlers when they complete
     */
    ensureTreeVisible() {
        if (this.allSquares.length > 0 && this.renderer) {
            // Always redraw the tree after interactions
            this.renderer.renderTree(this.allSquares, this.allTriangles, this.config);
        }
    }

    /**
     * Get a method reference for interaction handlers to call
     * This ensures the tree stays visible after pan/zoom operations
     */
    getPostInteractionCallback() {
        return () => this.ensureTreeVisible();
    }

    /**
     * Animation completion handler.
     * This method is called when the level-by-level animation naturally finishes.
     * It hides the level indicator and calls the user-provided onComplete callback.
     *
     * IMPORTANT: If the tree disappears after animation, especially after UI interactions
     * like minimizing a control panel or resizing the window, it's likely due to:
     * 1. The UI interaction (e.g., panel minimize) triggering a canvas clear (often by resizing
     *    the canvas element or an explicit renderer.clear() call in your main application logic)
     *    WITHOUT a subsequent redraw of the completed tree.
     * 2. The onComplete callback (set via setCallbacks) causing a canvas clear
     *    or resetting application state in a way that prevents the final tree from being displayed.
     * 3. A main application render loop (e.g., in main.js using requestAnimationFrame)
     *    that clears the canvas but does not redraw the contents of `this.allSquares`
     *    (which holds the completed tree) when `this.isAnimating` is false.
     *
     * SOLUTION: Use the renderCurrentTree() or redrawCompletedTree() methods after UI events
     * that might clear the canvas. For example, in your panel minimize handler:
     *
     * function onPanelMinimize() {
     *     // ... handle panel minimization ...
     *     // After any canvas operations that might clear it:
     *     if (animatedFractal.isTreeComplete()) {
     *         animatedFractal.redrawCompletedTree();
     *     }
     * }
     *
     * This class ensures `this.allSquares` contains the complete tree data upon completion,
     * and the last frame of the animation renders this tree. It does not clear the canvas
     * or `this.allSquares` as part of normal completion. The responsibility for rendering
     * the static, completed tree after UI events lies with the main application.
     * @private
     */
    _onAnimationComplete() {
        // Hide level indicator using cached element
        DOMUtils.addClass('level-indicator', 'hidden');

        // Call completion callback if set
        if (this.animationCallbacks.onComplete) {
            this.animationCallbacks.onComplete();
        }
    }

    /**
     * Delay utility for animation timing
     * @private
     */
    _delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Pause animation
     */
    pauseAnimation() {
        this.isAnimating = false;
    }

    /**
     * Resume animation
     */
    resumeAnimation() {
        this.isAnimating = true;
    } /**
     * Stop animation
     */
    stopAnimation() {
        this.isAnimating = false;
        this.currentLevel = 0;
        this.allSquares = [];
        this.allTriangles = [];
    }

    /**
     * Set animation callbacks
     * @param {Object} callbacks - Callback functions
     */
    setCallbacks(callbacks) {
        this.animationCallbacks = { ...callbacks };
    }
}
